import { z } from 'zod';

/**
 * Documentos contratuais emitidos pela esteira. O texto integral está em
 * apps/api/src/documentos/templates e é derivado do protótipo original.
 */
export const TIPOS_DOCUMENTO = ['carta', 'termo', 'regimento'] as const;
export const tipoDocumentoSchema = z.enum(TIPOS_DOCUMENTO);
export type TipoDocumento = z.infer<typeof tipoDocumentoSchema>;

export const DOCUMENTOS: Record<TipoDocumento, { nome: string; arquivo: string }> = {
  carta: { nome: 'Carta de Intenções e Pré-Reserva de Cadeira', arquivo: 'carta-de-intencoes' },
  termo: { nome: 'Termo de Aceite do Integrante', arquivo: 'termo-de-aceite' },
  regimento: { nome: 'Regimento Interno', arquivo: 'regimento-interno' },
};

/**
 * Campos da ficha que cada instrumento imprime. Mudar um campo desta lista depois da
 * emissão faz o PDF divergir do cadastro — e é isso que `impressaoDosDados` detecta.
 *
 * Se você acrescentar um campo ao template, acrescente aqui também, senão a divergência
 * passa despercebida.
 */
const CAMPOS_IMPRESSOS: Record<TipoDocumento, readonly string[]> = {
  carta: [
    'tipo',
    'razao',
    'doc',
    'endereco',
    'telefone',
    'representante',
    'email',
    'camada',
    'numero',
    'referencia',
    'valores',
  ],
  termo: [
    'tipo',
    'razao',
    'doc',
    'endereco',
    'telefone',
    'representante',
    'email',
    'camada',
    'numero',
    'referencia',
    'valores',
    'titulares',
    'curadoriaDecidida',
  ],
  regimento: ['razao', 'numero', 'referencia'],
};

/** Fonte dos campos, montada por quem chama a partir do candidato. */
export interface DadosImpressos {
  tipo: string;
  razao: string;
  doc: string;
  endereco: string;
  telefone: string;
  representante: string;
  email: string;
  camada: string;
  numero: string;
  referencia: string;
  /** "total|sinal|saldo" em centavos, do que vale para este candidato. */
  valores: string;
  titulares: string;
  curadoriaDecidida: string;
}

/**
 * Impressão digital dos dados que foram para o papel.
 *
 * Não é criptográfica — é detecção de mudança, e roda igual no navegador e na Lambda sem
 * depender de `node:crypto`. FNV-1a de 32 bits basta: colidir aqui só faria deixar de
 * avisar sobre uma edição, nunca afirmar algo falso sobre o documento.
 */
export function impressaoDosDados(tipo: TipoDocumento, dados: DadosImpressos): string {
  const texto = CAMPOS_IMPRESSOS[tipo]
    .map((campo) => `${campo}=${dados[campo as keyof DadosImpressos] ?? ''}`)
    .join('');

  let hash = 0x811c9dc5;
  for (let i = 0; i < texto.length; i++) {
    hash ^= texto.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export const documentoSchema = z.object({
  documentoId: z.string(),
  candidatoId: z.string(),
  tipo: tipoDocumentoSchema,
  /** Sequencial por candidato+tipo. Reemitir um documento cria a versão seguinte. */
  versao: z.number().int().positive(),
  /** Chave do objeto no bucket de documentos. */
  chaveS3: z.string(),
  /** versionId do objeto no S3 (bucket versionado) — prova de imutabilidade. */
  versionIdS3: z.string().optional(),
  /** SHA-256 do PDF, em hexadecimal. Vai impresso no rodapé do documento. */
  hash: z.string(),
  tamanhoBytes: z.number().int().nonnegative(),
  /**
   * Impressão digital dos campos que este PDF imprimiu. Se a ficha mudar depois, o
   * documento emitido deixa de refletir o cadastro — e a esteira precisa avisar, senão
   * ninguém percebe até alguém reler o arquivo.
   */
  impressaoDosDados: z.string(),
  emitidoEm: z.string().datetime(),
  emitidoPor: z.string(),
  /** Quando o PDF assinado voltou. Enquanto vazio, o documento foi apenas emitido. */
  assinadoRecebidoEm: z.string().datetime().optional(),
  assinadoRegistradoPor: z.string().optional(),
  /** Chave do PDF assinado no bucket, quando anexado. */
  chaveS3Assinado: z.string().optional(),
});
export type Documento = z.infer<typeof documentoSchema>;
